/**
 * Logo upload UI to upload logos for the color palette.
 * It depends on react-hook-form's FormProvider to send data up to its parent form
 * https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop
 */
import React, { DragEvent, DragEventHandler, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import LogoFilePreview from '../atoms/LogoFilePreview';
import LogoColorSelector from '../molecules/LogoColorSelector';

const LogoDragAndDrop = (): JSX.Element => {
  const [error, setError] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [logoFile, setLogoFile] = useState<File>(null);
  const logoImgRef = useRef(null);
  const {
    clearErrors,
    register,
    setValue,
    watch
  } = useFormContext();
  
  function resetLogo () {
    setLogoFile(null);
    setValue('file_upload', null);
    setValue('file', null);
    logoImgRef.current = null;
  }

  useEffect(() => {
    // Watch form data change locally within this component.
    // If paired with file_upload, change preview image
    watch((data, { name, type }) => {
      if (type === 'change') {
        if (name === 'file_upload') {
          if (!data.file_upload) {
            resetLogo();
            return;
          }
          // data.file_upload is a FileList; get the first file
          const file: File = data.file_upload[0];
          // If file is not empty, assume it is a real file
          // Assume the file_upload is checking file types
          if (file && file.size > 0) {
            resetLogo();
            setLogoFile(file);
            clearErrors('file_upload');
          } else {
            resetLogo();
            setLogoFile(null);
          }
        }
      }
    }, []);
  }, []);

  useEffect(() => {
    if (logoFile) {
      setValue('file', logoFile);
    } else {
      setValue('file', null);
    }
  }, [logoFile]);

  const handleClearImage: MouseEventHandler =
    () => {
      resetLogo();
    };

  const handleDrop: DragEventHandler =
    (e: DragEvent<HTMLDivElement>) => {
      // Prevent the file from being opened
      e.preventDefault();
      if (logoFile) {
        // Do not handle drop replace
        return;
      }
      const fileTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      let file;
      if (e.dataTransfer.items) {
        // Try using the DataTransferItemList interface to access the file(s)
        file = [...e.dataTransfer.items][0];
        if (file.kind === 'file') {
          file = file.getAsFile();
          setLogoFile(file);
        } else {
          setError('The item you dragged in does not appear to be a file. Please upload a .jpeg, .jpg, or .png.');
        }
      } else {
        // Use DataTransfer interface to access the file(s)
        file = [...e.dataTransfer.files][0];
        setLogoFile(file);
      }
      if (fileTypes.indexOf(file.type.toLowerCase()) === -1) {
        setError(`The file type of the file uploaded appears to be: ${file.type.replace('image/', '.')}. Please upload a .jpeg, .jpg, or .png.`);
      }
    };

  const handleDragOver: DragEventHandler =
    (e: DragEvent<HTMLDivElement>) => {
      // Prevent the file from being opened
      e.preventDefault();
      setIsHovered(true);
    };
  const handleMouseEnter: MouseEventHandler = () => {
    setIsHovered(true);
  };
  const handleMouseLeave: MouseEventHandler = () => {
    setIsHovered(false);
  };

  let content = <p className="ma-0 f-medium">Drag a .jpeg, .jpg, or .png file into this drop zone.</p>;
  if (error) {
    content = <p>{error}</p>;
  } else if (logoFile) {
    content =
      <div>
        <LogoFilePreview
          alt={'Preview of the logo for organization entered.'}
          file={logoFile}
          imgRef={logoImgRef}
        />
        <input type="hidden" {...register('file')} />
        <button className="block" type="button" onClick={handleClearImage}>Clear Image</button>
        {
          // NOTE: Sometimes UI hangs. Unsure why. It seems like it hangs until hover changes state because logoImgRef.current does not trigger updates on change?
          logoImgRef.current
            ? <LogoColorSelector logoImgRef={logoImgRef} />
            : <div className="f-small f-italic">Generating color palette...</div>
        }
      </div>;
  }

  const hoverStyle =
    isHovered
      ? {
          backgroundColor: 'rgb(5, 69, 111)',
          color: 'white',
        }
      : {
          backgroundColor: 'white',
          color: 'rgb(5, 69, 111)',
        };

  return (
    <div
      className="br-1 text-dark"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        ...hoverStyle,
        padding: '1rem',
        transition: 'background-color 0.25s ease-in-out',
      }}
    >
      {content}
    </div>
  );
};

export default LogoDragAndDrop;
